import { useState, useEffect, useCallback } from "react";
import api from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Switch } from "../../components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../components/ui/dialog";
import { Plus, Pencil } from "lucide-react";
import { toast } from "sonner";

export default function TaxRates() {
  const [taxes, setTaxes] = useState([]);
  const [modal, setModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ tax_name: "", rate_percent: 0, opera_trx_code: "", is_inclusive: false });

  const fetchTaxes = useCallback(async () => {
    const res = await api.get("/admin/taxes");
    setTaxes(res.data);
  }, []);

  useEffect(() => { fetchTaxes(); }, [fetchTaxes]);

  const openModal = (tax = null) => {
    setEditing(tax);
    setForm(tax ? { tax_name: tax.tax_name, rate_percent: tax.rate_percent, opera_trx_code: tax.opera_trx_code || "", is_inclusive: tax.is_inclusive }
      : { tax_name: "", rate_percent: 0, opera_trx_code: "", is_inclusive: false });
    setModal(true);
  };

  const save = async () => {
    try {
      if (editing) {
        await api.put(`/admin/taxes/${editing.id}`, form);
      } else {
        await api.post("/admin/taxes", form);
      }
      toast.success(editing ? "Tax rate updated" : "Tax rate created");
      setModal(false); fetchTaxes();
    } catch (err) { toast.error(err.response?.data?.detail || "Error"); }
  };

  const toggleTax = async (tax) => {
    await api.put(`/admin/taxes/${tax.id}/toggle`);
    toast.success(`Tax ${tax.is_active ? "deactivated" : "activated"}`);
    fetchTaxes();
  };

  return (
    <div className="space-y-6 animate-fade-in" data-testid="tax-rates">
      <div className="flex items-center justify-between">
        <h1 className="font-heading text-2xl font-bold tracking-tight">Tax Rates</h1>
        <Button onClick={() => openModal()} className="rounded-none bg-black text-white hover:bg-gray-800 text-xs h-9" data-testid="add-tax-button">
          <Plus className="w-3 h-3 mr-1" /> Add Tax Rate
        </Button>
      </div>

      <div className="bg-white border border-gray-200">
        <table className="w-full table-dense">
          <thead><tr className="border-b border-gray-100">
            <th className="text-left">Name</th><th className="text-right">Rate %</th><th className="text-left">Opera Code</th>
            <th className="text-center">Inclusive</th><th className="text-center">Active</th><th className="text-right">Actions</th>
          </tr></thead>
          <tbody>
            {taxes.map((tax) => (
              <tr key={tax.id} className="border-b border-gray-50 hover:bg-gray-50">
                <td className="font-semibold">{tax.tax_name}</td>
                <td className="text-right font-mono">{tax.rate_percent}%</td>
                <td className="font-mono text-xs text-gray-500">{tax.opera_trx_code || "—"}</td>
                <td className="text-center">{tax.is_inclusive ? <span className="text-[10px] font-bold text-blue-600">YES</span> : "—"}</td>
                <td className="text-center">
                  <Switch checked={tax.is_active} onCheckedChange={() => toggleTax(tax)} data-testid={`toggle-tax-${tax.id}`} />
                </td>
                <td className="text-right">
                  <Button variant="ghost" size="sm" onClick={() => openModal(tax)} className="h-7 text-xs" data-testid={`edit-tax-${tax.id}`}>
                    <Pencil className="w-3 h-3" />
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog open={modal} onOpenChange={setModal}>
        <DialogContent className="rounded-none border-gray-200 max-w-sm">
          <DialogHeader><DialogTitle className="font-heading">{editing ? "Edit" : "New"} Tax Rate</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs font-bold uppercase tracking-[0.15em] text-gray-500">Tax Name</Label>
              <Input value={form.tax_name} onChange={(e) => setForm({ ...form, tax_name: e.target.value })}
                className="mt-1 rounded-none" data-testid="tax-name-input" />
            </div>
            <div>
              <Label className="text-xs font-bold uppercase tracking-[0.15em] text-gray-500">Rate %</Label>
              <Input type="number" step="0.0001" value={form.rate_percent}
                onChange={(e) => setForm({ ...form, rate_percent: parseFloat(e.target.value) || 0 })}
                className="mt-1 rounded-none" data-testid="tax-rate-input" />
            </div>
            <div>
              <Label className="text-xs font-bold uppercase tracking-[0.15em] text-gray-500">Opera TRX Code</Label>
              <Input value={form.opera_trx_code} onChange={(e) => setForm({ ...form, opera_trx_code: e.target.value })}
                className="mt-1 rounded-none" />
            </div>
            <div className="flex items-center gap-3">
              <Switch checked={form.is_inclusive} onCheckedChange={(v) => setForm({ ...form, is_inclusive: v })} data-testid="tax-inclusive-switch" />
              <Label className="text-sm">Inclusive (tax embedded in price)</Label>
            </div>
          </div>
          <DialogFooter>
            <Button onClick={save} className="rounded-none bg-black text-white hover:bg-gray-800" data-testid="save-tax-button">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
